/**
 * Query keys and query options. Keys are the single place SSE invalidation
 * and optimistic updates agree on.
 */
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { type AppDetail, type AppSummary, type AppsTab, api, isApiError, isTransientError, type Page, type Person } from "./api";

export const qk = {
  me: ["me"] as const,
  authOptions: ["auth-options"] as const,
  connect: ["connect"] as const,
  aiConnections: ["ai-connections"] as const,
  billing: ["billing"] as const,
  deletionPlan: ["deletion-plan"] as const,
  apps: ["apps"] as const,
  appsList: (tab: AppsTab, q: string) => ["apps", "list", tab, q] as const,
  app: (appId: string) => ["app", appId] as const,
  appBySlug: (slug: string) => ["app-by-slug", slug] as const,
  sharing: (appId: string) => ["sharing", appId] as const,
  versions: (appId: string) => ["versions", appId] as const,
  secrets: (appId: string) => ["secrets", appId] as const,
  aiSource: (appId: string) => ["ai-source", appId] as const,
  drafts: (appId: string) => ["drafts", appId] as const,
  data: (appId: string) => ["data", appId] as const,
  rows: (appId: string) => ["rows", appId] as const,
  draft: (draftId: string) => ["draft", draftId] as const,
  upload: (uploadId: string) => ["upload", uploadId] as const,
  presence: (appId: string) => ["presence", appId] as const,
  suggest: (q: string) => ["people-suggest", q] as const,
  clientInfo: (clientId: string) => ["client-info", clientId] as const,
};

export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: ({ signal }) => api.getMe(signal),
  staleTime: 5 * 60_000,
  // Signed out (401) is an answer, not a failure; only ride out transient trouble.
  retry: (_count, error) => isTransientError(error),
  retryDelay: (attempt) => Math.min(15_000, 1000 * 2 ** attempt),
});

export function appBySlugQuery(slug: string) {
  return queryOptions({
    queryKey: qk.appBySlug(slug),
    queryFn: ({ signal }) => api.getAppBySlug(slug, signal),
    staleTime: 30_000,
    // 4xx answers (no access, deleted) are final; only retry network/server trouble.
    retry: (count, error) => (isTransientError(error) ? true : count < 2 && !(isApiError(error) && error.status >= 400 && error.status < 500)),
    retryDelay: (attempt) => Math.min(15_000, 1000 * 2 ** attempt),
  });
}

export const aiConnectionsQuery = queryOptions({
  queryKey: qk.aiConnections,
  queryFn: ({ signal }) => api.listAiConnections(signal),
  staleTime: 60_000,
});

export const billingQuery = queryOptions({
  queryKey: qk.billing,
  queryFn: ({ signal }) => api.getBilling(signal),
  staleTime: 60_000,
});

export const connectQuery = queryOptions({
  queryKey: qk.connect,
  queryFn: ({ signal }) => api.getConnect(signal),
  staleTime: Number.POSITIVE_INFINITY,
});

export function presenceQuery(appId: string) {
  return queryOptions({
    queryKey: qk.presence(appId),
    // Presence only ever arrives over SSE; the cache starts empty.
    queryFn: (): Person[] => [],
    staleTime: Number.POSITIVE_INFINITY,
    initialData: [] as Person[],
  });
}

type AppsPages = { pages: Page<AppSummary>[]; pageParams: (string | null)[] };

/** Apply `update` to an app in every cached Home list (for optimistic UI). */
export function updateAppInLists(client: QueryClient, appId: string, update: (app: AppSummary) => AppSummary | null): void {
  client.setQueriesData<AppsPages>({ queryKey: qk.apps }, (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        items: page.items.flatMap((item) => {
          if (item.id !== appId) return [item];
          const next = update(item);
          return next === null ? [] : [next];
        }),
      })),
    };
  });
}

/** Rename everywhere the app is cached. */
export function setAppNameEverywhere(client: QueryClient, app: { id: string; slug: string }, name: string): void {
  updateAppInLists(client, app.id, (a) => ({ ...a, name }));
  client.setQueryData<AppDetail>(qk.appBySlug(app.slug), (old) => (old ? { ...old, name } : old));
}

/** Refetch everything about one app (used by SSE and after mutations). */
export function invalidateApp(client: QueryClient, appId: string): void {
  void client.invalidateQueries({ queryKey: qk.apps });
  void client.invalidateQueries({ queryKey: qk.app(appId) });
  void client.invalidateQueries({
    predicate: (q) => q.queryKey[0] === "app-by-slug" && (q.state.data as AppDetail | undefined)?.id === appId,
  });
}
